import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { selectors, withOffset } from '../../Stream';
import { requestPuppy } from '@wix/da-http-client';
import { MediaSourceType } from '../types';
import { getCurrentMediaSource, getCurrentTierId } from '../selectors';
import type { FetchAction } from '../../Stream/base/types';
import type { PapiDaSharedTierContent } from '@wix/da-papi-types';
import { normalizeDeviations } from '../../redux/normalizr/helpers';
import { FetchType } from '../../Stream/base/types';

export function fakeResponse({ params }): any {
  if (process.env.NODE_ENV !== 'production') {
    const { deviationsByType } = require('@wix/da-sample-data'); // eslint-disable-line global-require
    const { Chance } = require('chance'); // eslint-disable-line global-require
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }

    const { folderId, offset, limit } = params;

    if (folderId === -999) {
      return {
        hasMore: false,
        nextOffset: 0,
        deviations: [],
      };
    }

    const randomDeviations = new Chance().pickset(
      deviationsByType.pixel
        .concat(deviationsByType.statuses)
        .concat(deviationsByType.image)
        .concat(deviationsByType.edgeCases)
        .concat(deviationsByType.journal)
        .concat(deviationsByType.literature),
      limit
    );

    return {
      hasMore: offset + limit < deviationsByType.image.length,
      nextOffset: offset + limit,
      deviations: randomDeviations.slice(0, limit),
    };
  }
}

function* fetch(url: string, params: object) {
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url,
      params,
    },
    fakeResponse
  );
  return response;
}

function* handleDeviationStreamFetch(action: FetchAction): SagaIterator {
  const { streamId } = action;

  const {
    tierId,
    mediaSource,
    offset,
    limit,
  }: {
    tierId: ReturnType<typeof getCurrentTierId>;
    mediaSource: ReturnType<typeof getCurrentMediaSource>;
    offset: ReturnType<typeof withOffset.selectors.getNextOffset>;
    limit: ReturnType<typeof selectors.getItemsPerFetch>;
  } = yield select(state => ({
    tierId: getCurrentTierId(state),
    mediaSource: getCurrentMediaSource(state),
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
  }));

  const url = 'tier/content';
  const params = {
    tier_deviationid: tierId,
    section:
      mediaSource === MediaSourceType.TIER_DEVIATIONS ? 'deviations' : 'posts',
    offset,
    limit,
    mature_content: true,
  };

  const response: PapiDaSharedTierContent | false = yield call(
    fetch,
    url,
    params
  );

  if (!response) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch',
      })
    );
    return;
  }

  const { hasMore, deviations: results, nextOffset } = response;
  const normalized = results && normalizeDeviations(results);

  yield put(
    withOffset.actions.fetchSuccessDeduped({
      streamId,
      hasMore: results ? !!hasMore : false,
      items: normalized?.result ?? [],
      entities: normalized?.entities ?? {},
      nextOffset: nextOffset ?? undefined,
    })
  );
}

export default handleDeviationStreamFetch;
